import { useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { createTrip, deleteTrip, editTrip } from '../../features/trips/tripSlice';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody,
    Table, TableContainer, Thead, Tbody, Tr, Th, Td, Button, Stack, WrapItem, Tooltip,
    Input, IconButton, Text, Box, Center,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon, CheckIcon, CloseIcon } from '@chakra-ui/icons';

const TripForm = () => {
    const { isLoading, isError, message, actionType } = useSelector((state) => state.trips);
    const dispatch = useDispatch();

    const formik = useFormik({
        initialValues: { name: '' },
        validationSchema: Yup.object({
            name: Yup.string()
                .min(3, "Must have a minimum of 3 characters")
                .max(40, "Cannot be longer than 40 characters")
                .required("Trip name is required")
        }),
        onSubmit: (values, { resetForm }) => {
            dispatch(createTrip({ name: values.name }));
            resetForm();
        }
    });

    return (
        <Box mt={6}>
            <Text fontSize="lg" fontWeight="bold" mb={2}>Add new trip:</Text>
            <Stack direction='row' spacing={4}>
                <Input
                    placeholder='Enter trip name'
                    id="name"
                    name="name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={formik.errors.name && formik.touched.name}
                />
                <Button
                    onClick={formik.handleSubmit}
                    isLoading={isLoading && actionType === 'create'}
                    variant='outline'
                >
                    Create
                </Button>
            </Stack>
            {formik.errors.name && formik.touched.name && <Text color="red.500" mt={2}>{formik.errors.name}</Text>}
            {isError && <Text color="red.500" mt={2}>Error: {message}</Text>}
        </Box>
    );
};

const TripTable = () => {
    const { trips, isLoading, actionType } = useSelector((state) => state.trips);
    const dispatch = useDispatch();
    const [editState, setEditState] = useState({ id: null, name: '' }); // This can probably be refactored/integrated w current action but no time
    const [currentAction, setCurrentAction] = useState({ type: null, id: null });

    const handleDelete = (tripId) => {
        dispatch(deleteTrip(tripId));
        setCurrentAction({ type: 'delete', id: tripId });
    };

    const handleEdit = (trip) => {
        setEditState({ id: trip.value, name: trip.label });
    };

    const handleSave = () => {
        dispatch(editTrip({ tripId: editState.id, name: editState.name }));
        setEditState({ id: null, name: '' });
        setCurrentAction({ type: 'edit', id: editState.id });
    };

    const handleCancel = () => {
        setEditState({ id: null, name: '' });
    };

    return (
        <Box mt={4} w='100%'>
            <Text fontSize="lg" fontWeight="bold">Your trips:</Text>
            {trips.length === 0 ? (
                <Center>
                    <Text>No trips added.</Text>
                </Center>
            ) : (
                <TableContainer mt={3}>
                    <Table variant='simple'>
                        <Thead>
                            <Tr>
                                <Th>Name</Th>
                                <Th>Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {trips.map((trip) => (
                                <Tr key={trip.value}>
                                    <Td minW='250px'>
                                        {editState.id === trip.value ? (
                                            <Input
                                                value={editState.name}
                                                onChange={(e) => setEditState({ ...editState, name: e.target.value })}
                                            />
                                        ) : (
                                            trip.label
                                        )}
                                    </Td>
                                    <Td>
                                        <Stack direction='row' align='center' spacing={2}>
                                            {editState.id === trip.value ? (
                                                <>
                                                    <IconButton icon={<CheckIcon />} onClick={handleSave} />
                                                    <IconButton icon={<CloseIcon />} onClick={handleCancel} />
                                                </>
                                            ) : (
                                                <>
                                                    <IconButton
                                                        icon={<EditIcon />}
                                                        onClick={() => handleEdit(trip)}
                                                        variant='outline'
                                                        isLoading={isLoading && actionType === 'edit' && currentAction.id === trip.value}
                                                    />
                                                    <IconButton
                                                        icon={<DeleteIcon />}
                                                        onClick={() => handleDelete(trip.value)}
                                                        variant='outline'
                                                        isLoading={isLoading && actionType === 'delete' && currentAction.id === trip.value}
                                                    />
                                                </>
                                            )}
                                        </Stack>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}

export default function ManageTripsModal({ isOpen, setIsOpen }) {
    return (
        <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="lg">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Manage trips</ModalHeader>
                <ModalCloseButton />
                <ModalBody p={4}>
                    <TripTable />
                    <TripForm />
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}
